import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { endOfMonth, startOfMonth } from "date-fns";
import { fr } from "date-fns/locale/fr";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "styled-components";
import { CalendarWeek } from "../../materialUi/materialUi";
import MaterialUiThemeProvider from "../../../providers/MaterialUiThemeProvider";
import { colors } from "../../../styles/figmaColors";

export type MaterialUiMonthPickerProps = {
  value?: [start: string, end: string];
  onChange: (dates: [start: string, end: string]) => void;
};

export function MaterialUiMonthPicker({
  onChange,
  value,
}: MaterialUiMonthPickerProps) {
  const [open, setOpen] = useState(false);
  const isOpeningRef = useRef(false);
  const [pending, setPending] = useState<Date | null>(null);

  const pickerValue = useMemo(() => {
    if (!value?.[0]) return startOfMonth(new Date());
    return startOfMonth(new Date(value[0]));
  }, [value]);

  const commitMonth = useCallback(
    (date: Date) => {
      const start = startOfMonth(date);
      const end = endOfMonth(date);
      start.setHours(12, 0, 0, 0);
      end.setHours(12, 0, 0, 0);
      onChange([start.toISOString(), end.toISOString()]);
    },
    [onChange],
  );

  const handleAccept = useCallback(
    (date: Date | null) => {
      const selectedMonth = date ?? pending;
      if (!selectedMonth) return;
      commitMonth(selectedMonth);
      setOpen(false);
    },
    [commitMonth, pending],
  );

  useEffect(() => {
    if (value?.[0]) return;
    commitMonth(pickerValue);
  }, [commitMonth, pickerValue, value]);

  return (
    <MaterialUiThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
        <DatePicker
          views={["month", "year"]}
          openTo="month"
          value={pickerValue}
          maxDate={new Date()}
          onChange={(date) => setPending(date ? startOfMonth(date) : null)}
          onAccept={handleAccept}
          open={open}
          onOpen={() => {
            isOpeningRef.current = true;
            setOpen(true);
            setPending(pickerValue);
          }}
          onClose={() => {
            isOpeningRef.current = false;
            setOpen(false);
          }}
          slots={{
            openPickerIcon: () => (
              <StyledIcon>
                <CalendarWeek />
              </StyledIcon>
            ),
          }}
          sx={{
            "& .MuiInputBase-root .MuiOutlinedInput-notchedOutline": {
              outline: "none",
              border: `0.0625rem solid ${colors["colors/borders/cells/cells"]} !important`,
              boxShadow: "2px 1px 4px 0px rgba(122, 122, 122, 0.08)",
            },
            "& .MuiInputBase-root .MuiOutlinedInput-root:hover ": {
              backgroundColor: `${colors["colors/button/secondary/hover"]} !important`,
            },
            "& .MuiInputBase-input-MuiOutlinedInput-input": {
              minWidth: "12rem",
            },
            "& .MuiInputBase-root.MuiOutlinedInput-root": {
              backgroundColor: "white",
            },
          }}
          slotProps={{
            textField: {
              margin: "none",
              onBlur: () => {
                if (isOpeningRef.current) return;
                if (!pending) return;
                commitMonth(pending);
                setOpen(false);
              },
              inputProps: {
                onClick: () => {
                  isOpeningRef.current = true;
                  setOpen(true);
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </MaterialUiThemeProvider>
  );
}

const StyledIcon = styled.div`
  & svg {
    width: 1rem;
    height: 1.2rem;
    fill: ${colors["colors/text/black"]} !important;
  }
  display: flex;
`;
